import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

// Standard page template for consistent layouts
export interface PageTemplateProps {
  title?: string; // Kept for backward compatibility but not displayed
  subtitle?: string; // Kept for backward compatibility but not displayed
  action?: React.ReactNode; // Kept for backward compatibility but not displayed
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  sx?: SxProps<Theme>;
}

export const PageTemplate: React.FC<PageTemplateProps> = ({
  children,
  maxWidth = 'lg',
  sx = {}
}) => {
  return (
    <Box
      sx={{
        maxWidth: maxWidth === false ? 'none' : (maxWidth === 'xs' ? '448px' : maxWidth === 'sm' ? '600px' : maxWidth === 'md' ? '900px' : maxWidth === 'lg' ? '100%' : '100%'),
        width: '100%',
        boxSizing: 'border-box',
        px: { xs: 1.5, sm: 2, md: 2.5 },
        py: 2.5,
        ...sx,
      }}
    >
      {/* PageHeader removed to hide page titles */}
      {children}
    </Box>
  );
};

// Standard section component for consistent content blocks
export interface SectionProps {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const Section: React.FC<SectionProps> = ({
  title,
  subtitle,
  action,
  children,
  sx = {}
}) => {
  return (
    <Box sx={{ mt: 6, mb: 4, ...sx }}>
      {(title || subtitle || action) && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            {title && (
              <Box
                component="h2"
                sx={{
                  m: 0,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  fontWeight: 700,
                  color: 'grey.800',
                  fontFamily: 'inherit',
                  mb: subtitle ? 1 : 0,
                }}
              >
                {title}
              </Box>
            )}
            {subtitle && (
              <Box
                component="p"
                sx={{
                  m: 0,
                  fontSize: '1rem',
                  color: 'text.secondary',
                  maxWidth: '600px',
                }}
              >
                {subtitle}
              </Box>
            )}
          </Box>
          {action && (
            <Box sx={{ flexShrink: 0, ml: 2 }}>
              {action}
            </Box>
          )}
        </Box>
      )}
      {children}
    </Box>
  );
};

export default {
  PageTemplate,
  Section,
};
